import { useRef, useEffect } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import gettext, { ngettext } from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import TextField from '@sdk/components/forms/TextField';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSection from '../SettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import { TabType } from '../../types';
import { useSettingsErrorsContext } from '../../SettingsErrorContext';
import { useUserOrgSettings } from '../../UserOrgSettingsContext';

const StyledTextField = styled(TextField)`
  max-width: 205px;
`;

const Restrictions: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { userOrgSettings, onUserOrgSettingsChange } = useUserOrgSettings();
  const { clockInEarlyEnabled, clockInEarlyMinutes, clockInAdHocEnabled } = userOrgSettings;
  const { toggleError, isFieldValid } = useSettingsErrorsContext();
  const { currentUser } = useAuth();
  const focusFieldRef = useRef(null);
  const clockInEarlyEnabledPrevState = useRef(clockInEarlyEnabled);

  useEffect(() => {
    if (!clockInEarlyEnabledPrevState.current && clockInEarlyEnabled) {
      focusFieldRef.current.focus();
    }

    clockInEarlyEnabledPrevState.current = clockInEarlyEnabled;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clockInEarlyEnabled]);

  const onEarlyClockInChange = (value: boolean): void => {
    if (!value) {
      onUserOrgSettingsChange({ clockInEarlyEnabled: value, clockInEarlyMinutes: 0 });
      toggleError(TabType.timeClock, 'clockInEarlyMinutes', false);
      return;
    }

    onUserOrgSettingsChange({ clockInEarlyEnabled: value, clockInEarlyMinutes: '' });
  };

  const validClockInEarlyMinutes = (value: number | string): boolean => {
    return (
      value !== '' &&
      validateNumber(value, {
        [ValidateOptions.integer]: true,
        [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
      })
    );
  };

  const onClockInEarlyMinutesChange = (value: string | number): void => {
    const isValid = validClockInEarlyMinutes(value);

    onUserOrgSettingsChange({
      clockInEarlyMinutes: isValid ? Number(value) : value,
      clockInEarlyEnabled: isValid ? value > 0 : clockInEarlyEnabled,
    });
    toggleError(TabType.timeClock, 'clockInEarlyMinutes', !validClockInEarlyMinutes(value));
  };

  return (
    <>
      <SettingsSection withSeparator>
        <CollapsibleSettingsSection
          isActive={clockInEarlyEnabled}
          headerItem={
            <SettingsSwitcherRow
              disabled={!currentUser.isAdmin}
              title={gettext('Early clock in')}
              subtitle={gettext(
                'Allow employees to clock in prior to the scheduled start of the shift.'
              )}
              value={clockInEarlyEnabled}
              onChange={(value): void => {
                onEarlyClockInChange(value);
              }}
            />
          }
          contentItem={
            <RowSpaceBetween mTop={20}>
              <Column>
                <Text14 themeColor={ThemeColorType.gray}>
                  <TranslateWithComponents
                    replaceComponents={{
                      component: (
                        <Text14 themeColor={ThemeColorType.primary}>
                          {Number(clockInEarlyMinutes) || 'X'}
                        </Text14>
                      ),
                    }}>
                    {ngettext(
                      'Employees can clock in [[component]] minute before shift starts',
                      'Employees can clock in [[component]] minutes before shift starts',
                      Number(clockInEarlyMinutes)
                    )}
                  </TranslateWithComponents>
                </Text14>
              </Column>
              <StyledTextField
                ref={focusFieldRef}
                skipErrorMessage
                maxLength={10}
                disabled={!currentUser.isAdmin}
                value={clockInEarlyMinutes}
                postfix={gettext('Minutes')}
                onChange={(e): void => {
                  onClockInEarlyMinutesChange(e.currentTarget.value);
                }}
                onBlur={(): void =>
                  toggleError(
                    TabType.timeClock,
                    'clockInEarlyMinutes',
                    !validClockInEarlyMinutes(clockInEarlyMinutes)
                  )
                }
                invalid={!isFieldValid('clockInEarlyMinutes')}
              />
            </RowSpaceBetween>
          }
        />
      </SettingsSection>

      <SettingsSwitcherRow
        disabled={!currentUser.isAdmin}
        withSeparator
        title={gettext('Clock in without a scheduled shift')}
        subtitle={gettext(`Allow employees to clock in if they don't have a shift scheduled.`)}
        value={clockInAdHocEnabled}
        onChange={(value): void => onUserOrgSettingsChange({ clockInAdHocEnabled: value })}
      />
    </>
  );
};

export default Restrictions;
